import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { userService } from '../services/userService';
import { productService } from '../services/productService';
import { reviewService } from '../services/reviewService';
import { productUrl } from '../utils/seo';
import UserLink from '../components/UserLink';
import FilterModal from '../components/FilterModal';

function Stars({ rating }) {
  return (
    <span className="stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className={n <= Math.round(rating) ? 'star-filled' : 'star-empty'}>★</span>
      ))}
    </span>
  );
}

const PAGE_SIZE = 12;

function SellerProfile() {
  const { username } = useParams();
  const [profile, setProfile] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Products + infinite scroll
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const sentinelRef = useRef(null);

  // Search & filters
  const [searchInput, setSearchInput] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({
    minPrice: '', maxPrice: '',
    hasInterested: 0,
    priceType: null,
    condition: null,
    madeToOrder: null,
  });
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  // Count active advanced filters
  const activeFilterCount = [
    filters.minPrice !== '' && filters.minPrice !== undefined,
    filters.maxPrice !== '' && filters.maxPrice !== undefined,
    filters.hasInterested !== 0,
    filters.priceType !== null,
    filters.condition !== null,
    filters.madeToOrder !== null && filters.madeToOrder !== undefined,
  ].filter(Boolean).length;

  // Load profile, reviews, and categories once
  useEffect(() => {
    setLoading(true);
    Promise.all([
      userService.getPublicProfile(username),
      reviewService.getSellerReviews(username),
      productService.getSellerCategories(username),
    ])
      .then(([profileRes, reviewsRes, catRes]) => {
        setProfile(profileRes.data);
        setReviews(reviewsRes.data?.content || reviewsRes.data || []);
        setCategories(catRes.data || []);
      })
      .catch(() => setError('No se pudo cargar el perfil'))
      .finally(() => setLoading(false));
  }, [username]);

  // Fetch a page of products
  const fetchPage = useCallback((pageNum, reset) => {
    if (loadingProducts) return;
    setLoadingProducts(true);
    const params = { page: pageNum, size: PAGE_SIZE };
    if (activeSearch) params.search = activeSearch;
    if (activeCategory) params.category = activeCategory;
    if (filters.minPrice) params.minPrice = filters.minPrice;
    if (filters.maxPrice) params.maxPrice = filters.maxPrice;
    if (filters.priceType) params.priceType = filters.priceType;
    if (filters.condition) params.condition = filters.condition;
    if (filters.madeToOrder !== null && filters.madeToOrder !== undefined) params.madeToOrder = filters.madeToOrder;
    if (filters.hasInterested !== 0) params.hasInterested = filters.hasInterested;

    productService.getBySeller(username, params)
      .then((res) => {
        const data = res.data;
        const items = data.content || data || [];
        setProducts((prev) => reset ? items : [...prev, ...items]);
        setHasMore(!data.last);
        setPage(pageNum);
      })
      .catch(() => {})
      .finally(() => setLoadingProducts(false));
  }, [username, activeSearch, activeCategory, filters, loadingProducts]);

  // Reset and fetch first page when search/category/filters change
  useEffect(() => {
    setProducts([]);
    setPage(0);
    setHasMore(true);
    fetchPage(0, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, activeSearch, activeCategory, filters]);

  // Infinite scroll observer
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingProducts) {
          fetchPage(page + 1, false);
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadingProducts, page, fetchPage]);

  const handleSearch = (e) => {
    e.preventDefault();
    const lower = searchInput.toLowerCase().trim();
    if (/\busados?\b/.test(lower)) {
      setFilters((prev) => ({ ...prev, condition: 'USED' }));
    } else if (/\bnuevos?\b/.test(lower)) {
      setFilters((prev) => ({ ...prev, condition: 'NEW' }));
    }
    setActiveSearch(searchInput.trim());
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setActiveSearch('');
    setActiveCategory('');
    setFilters({ minPrice: '', maxPrice: '', hasInterested: 0, priceType: null, condition: null, madeToOrder: null });
  };

  if (loading) return <div className="loading"><span className="spinner" /></div>;
  if (error) return <div className="error-state">{error}</div>;
  if (!profile) return null;

  const waUrl = profile.whatsapp
    ? `https://wa.me/${profile.whatsapp.replace(/\D/g, '')}`
    : null;

  const hasAnyFilter = activeSearch || activeCategory || activeFilterCount > 0;

  const sellerTitle = profile.profession
    ? `${profile.username} — ${profile.profession} — MercadoLibertario`
    : `${profile.username} — MercadoLibertario`;
  const sellerDesc = profile.bio
    ? profile.bio.substring(0, 160)
    : `Perfil de ${profile.username} en MercadoLibertario. ${profile.profession || 'Vendedor'} en ${profile.department || 'Uruguay'}.`;
  const profileUrl = `${window.location.origin}/profile/${username}`;

  const personJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: profile.username,
    url: profileUrl,
    ...(profile.profilePhotoUrl && { image: profile.profilePhotoUrl }),
    ...(profile.bio && { description: profile.bio.substring(0, 200) }),
    ...(profile.department && { address: { '@type': 'PostalAddress', addressRegion: profile.department, addressCountry: 'UY' } }),
    ...(profile.profession && { jobTitle: profile.profession }),
    ...(reviews.length > 0 && profile.rating && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: profile.rating.toFixed(1),
        reviewCount: reviews.length,
        bestRating: 5,
        worstRating: 1,
      },
    }),
  };

  return (
    <div className="main-content" style={{ maxWidth: 820 }}>
      <Helmet>
        <title>{sellerTitle}</title>
        <meta name="description" content={sellerDesc} />
        <link rel="canonical" href={profileUrl} />
        <meta property="og:type" content="profile" />
        <meta property="og:title" content={sellerTitle} />
        <meta property="og:description" content={sellerDesc} />
        <meta property="og:image" content={profile.profilePhotoUrl || `${window.location.origin}/logo512.png`} />
        <meta property="og:url" content={profileUrl} />
        <meta property="og:site_name" content="MercadoLibertario" />
        <script type="application/ld+json">{JSON.stringify(personJsonLd)}</script>
      </Helmet>

      {/* Header */}
      <div className="card card-xl" style={{ marginBottom: '1.5rem' }}>
        <div className="profile-header">
          {profile.profilePhotoUrl
            ? <img src={profile.profilePhotoUrl} alt={profile.username} className="profile-photo" />
            : <div className="profile-photo-placeholder">👤</div>
          }
          <div className="profile-info">
            <div className="profile-name">{profile.username}</div>
            {profile.profession && (
              <div className="profile-profession">{profile.profession}</div>
            )}
            {(profile.department || profile.city) && (
              <div className="profile-location">
                📍 {[profile.city, profile.department].filter(Boolean).join(', ')}
              </div>
            )}
            {profile.bio && <div className="profile-bio">{profile.bio}</div>}

            {profile.avgRating > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.6rem' }}>
                <Stars rating={profile.avgRating} />
                <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                  {profile.avgRating.toFixed(1)} ({profile.reviewCount} {profile.reviewCount === 1 ? 'reseña' : 'reseñas'})
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Contact buttons */}
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          {waUrl && (
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn contact-btn-wa btn-sm"
            >
              WhatsApp
            </a>
          )}
          {profile.phone && (
            <a href={`tel:${profile.phone}`} className="btn btn-secondary btn-sm">
              📞 {profile.phone}
            </a>
          )}
          {profile.email && (
            <a href={`mailto:${profile.email}`} className="btn btn-secondary btn-sm">
              ✉️ {profile.email}
            </a>
          )}
        </div>
      </div>

      {/* Products section */}
      <section style={{ marginBottom: '2rem' }}>
        <h2 className="section-title" style={{ marginBottom: '1rem' }}>
          Publicaciones activas
        </h2>

        {/* Search bar */}
        <form onSubmit={handleSearch} className="search-bar" style={{ marginBottom: '1rem' }}>
          <input
            type="text"
            className="search-input"
            placeholder="Buscar en publicaciones..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <button type="submit" className="btn btn-primary btn-sm">Buscar</button>
          {hasAnyFilter && (
            <button type="button" className="btn btn-secondary btn-sm" onClick={handleClearSearch}>
              Limpiar
            </button>
          )}
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => setFilterModalOpen(true)}
            style={{ position: 'relative' }}
          >
            Filtros
            {activeFilterCount > 0 && (
              <span className="filter-badge">{activeFilterCount}</span>
            )}
          </button>
        </form>

        {/* Category pills */}
        {categories.length > 1 && (
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
            <button
              className={`btn btn-sm ${!activeCategory ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setActiveCategory('')}
            >
              Todas
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                className={`btn btn-sm ${activeCategory === cat ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setActiveCategory(activeCategory === cat ? '' : cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {/* Product grid */}
        {products.length > 0 ? (
          <div className="product-grid">
            {products.map((p) => (
              <Link
                key={p.id}
                to={productUrl(p)}
                className="product-card"
                style={{ textDecoration: 'none' }}
              >
                <div className="product-card-img">
                  {p.imageUrls?.length > 0
                    ? <img src={p.imageUrls[0]} alt={p.title} loading="lazy" decoding="async" />
                    : <div className="product-card-img-placeholder">📦</div>
                  }
                </div>
                <div className="product-card-body">
                  <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.4rem', flexWrap: 'wrap' }}>
                    <span className="badge badge-category">{p.category}</span>
                    {p.condition && (
                      <span className={`badge ${p.condition === 'NEW' ? 'badge-new' : 'badge-used'}`}>
                        {p.condition === 'NEW' ? 'Nuevo' : 'Usado'}
                      </span>
                    )}
                  </div>
                  <h3>{p.title}</h3>
                  <div className="product-price">
                    {p.priceOnConsultation ? 'Precio a consultar' : `${p.currency === 'USD' ? 'US$' : '$'}${Number(p.price).toLocaleString('es-UY')}`}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          !loadingProducts && (
            <div className="no-results">
              {hasAnyFilter
                ? 'No se encontraron publicaciones con esos filtros'
                : 'Este usuario no tiene publicaciones activas'}
            </div>
          )
        )}

        {/* Infinite scroll sentinel */}
        {hasMore && <div ref={sentinelRef} style={{ height: 1 }} />}
        {loadingProducts && (
          <div className="loading" style={{ padding: '1rem' }}>
            <span className="spinner" />
          </div>
        )}
      </section>

      {/* Filter modal */}
      {filterModalOpen && (
        <FilterModal
          filters={filters}
          onApply={setFilters}
          onClose={() => setFilterModalOpen(false)}
        />
      )}

      {/* Reviews */}
      {reviews.length > 0 && (
        <section>
          <h2 className="section-title" style={{ marginBottom: '1rem' }}>Reseñas</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {reviews.map((r) => (
              <div key={r.id} className="card">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                  <Stars rating={r.rating} />
                  <UserLink
                    username={r.reviewerUsername}
                    rating={r.reviewerRating}
                    reviewCount={r.reviewerReviewCount}
                  />
                  {r.productTitle && (
                    <Link to={r.productId ? productUrl({ id: r.productId, title: r.productTitle }) : '#'} style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      {r.productTitle}
                    </Link>
                  )}
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem', marginLeft: 'auto' }}>
                    {new Date(r.createdAt).toLocaleDateString('es-UY')}
                  </span>
                </div>
                {r.comment && (
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: 1.6 }}>
                    {r.comment}
                  </p>
                )}
                {r.sellerResponse && (
                  <div className="info-box" style={{ marginTop: '0.75rem' }}>
                    <strong>Respuesta del vendedor:</strong> {r.sellerResponse}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {products.length === 0 && reviews.length === 0 && !loadingProducts && !hasAnyFilter && (
        <div className="no-results">Este usuario todavía no tiene publicaciones</div>
      )}
    </div>
  );
}

export default SellerProfile;
